import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ClipboardCheck, Plus, CheckCircle2, X, LogIn, LogOut, Loader2, Phone, DoorOpen, Building2, ExternalLink, Mail, LayoutGrid, List, Eye, AlertTriangle } from "lucide-react";

const TABS = [
  { key: "all", label: "All" },
  { key: "PENDING_EMPLOYEE_APPROVAL", label: "Awaiting Host" },
  { key: "PENDING_RECEPTION_VERIFICATION", label: "To Verify" },
  { key: "VERIFIED", label: "Verified" },
  { key: "CHECKED_IN", label: "Inside" },
  { key: "CHECKED_OUT", label: "Checked Out" },
];

export default function Visitors() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [tab, setTab] = useState("all");
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [view, setView] = useState("card");
  const [detail, setDetail] = useState(null);
  const [passQr, setPassQr] = useState(null);

  const load = () => {
    const q = tab === "all" ? "" : `?status=${tab}`;
    api.get(`/visitors${q}`).then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { load(); }, [tab]);
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setPassQr(null);
    if (detail?.pass_link) {
      const token = detail.pass_link.split("/").pop();
      api.get(`/public/visitor/pass/${token}`).then((r) => setPassQr(r.data.qr)).catch(() => {});
    }
  }, [detail]);

  const act = async (v, action) => {
    setBusy(`${v.id}-${action}`);
    try { await api.post(`/visitors/${v.id}/${action}`); toast.success("Done"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const openNew = () => { setForm({ branch_id: user.branch_id || undefined }); setOpen(true); };
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.post("/visitors", form); toast.success("Visitor added — host notified"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!list) return <Loader />;
  const canManual = ["SUPER_ADMIN", "COMPANY_ADMIN", "BRANCH_ADMIN", "RECEPTIONIST"].includes(user.role);

  const Actions = ({ v }) => (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={() => setDetail(v)} data-testid={`view-visitor-${v.id}`}><Eye size={14} className="mr-1" /> View</Button>
      {["APPROVED_BY_EMPLOYEE", "PENDING_RECEPTION_VERIFICATION"].includes(v.status) && (
        <>
          <Button size="sm" onClick={() => act(v, "verify")} disabled={busy} data-testid={`verify-btn-${v.id}`} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 size={14} className="mr-1" /> Verify</Button>
          <Button size="sm" variant="outline" onClick={() => act(v, "reject")} disabled={busy} className="border-rose-300 text-rose-700"><X size={14} /></Button>
        </>
      )}
      {v.status === "VERIFIED" && <Button size="sm" onClick={() => act(v, "checkin")} disabled={busy} data-testid={`checkin-btn-${v.id}`} className="bg-blue-600 hover:bg-blue-700"><LogIn size={14} className="mr-1" /> Check In</Button>}
      {v.status === "CHECKED_IN" && <Button size="sm" onClick={() => act(v, "checkout")} disabled={busy} data-testid={`checkout-btn-${v.id}`} variant="outline"><LogOut size={14} className="mr-1" /> Check Out</Button>}
      {["PENDING_EMPLOYEE_APPROVAL", "HOLD"].includes(v.status) && v.approval_link && (
        <a href={v.approval_link} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`approval-link-${v.id}`}><ExternalLink size={13} className="mr-1" /> Host Link</Button></a>
      )}
      {v.pass_link && <a href={v.pass_link} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="text-blue-600">Pass</Button></a>}
    </div>
  );

  return (
    <div>
      <PageHeader title="Visitor Management" subtitle="Live visitor queue — approvals, verification and check-in/out."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              <button onClick={() => setView("card")} data-testid="visitor-view-card" className={`px-2.5 py-2 transition-colors ${view === "card" ? "bg-navy-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`} title="Card view"><LayoutGrid size={16} /></button>
              <button onClick={() => setView("list")} data-testid="visitor-view-list" className={`px-2.5 py-2 transition-colors ${view === "list" ? "bg-navy-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`} title="List view"><List size={16} /></button>
            </div>
            {canManual && <Button onClick={openNew} data-testid="add-visitor-btn" className="bg-navy-900 hover:bg-navy-800"><Plus size={16} className="mr-1.5" /> Manual Add</Button>}
          </div>
        } />

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList className="flex-wrap h-auto">
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`visitor-tab-${t.key}`}>{t.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {list.length === 0 ? <EmptyState icon={ClipboardCheck} title="No visitors here" subtitle="Visitor requests will appear as they arrive." /> : view === "card" ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((v) => (
            <div key={v.id} className="bg-white rounded-2xl border border-slate-200 p-5 sf-fade-up" data-testid={`visitor-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display font-bold text-navy-900 text-lg">{v.visitor_name}</h3>
                  <p className="text-sm text-slate-500 flex items-center gap-1"><Building2 size={13} /> {v.visitor_company}</p>
                </div>
                <StatusBadge status={v.status} />
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-600">
                <p className="flex items-center gap-1.5"><Phone size={13} className="text-slate-400" /> {v.visitor_phone}</p>
                <p className="flex items-center gap-1.5"><ClipboardCheck size={13} className="text-slate-400" /> Meet: <b className="text-navy-900">{v.person_to_meet}</b></p>
                <p className="flex items-center gap-1.5"><Mail size={13} className="text-slate-400" /> Host: <b className="text-navy-900" data-testid={`visitor-host-email-${v.id}`}>{v.person_email || "—"}</b></p>
                <p className="flex items-center gap-1.5"><DoorOpen size={13} className="text-slate-400" /> Cabin: <b className="text-navy-900" data-testid={`visitor-cabin-${v.id}`}>{v.cabin_number || "—"}</b></p>
                <p className="text-slate-500">{v.purpose}</p>
                <p className="text-xs text-slate-400">{new Date(v.created_at).toLocaleString()}</p>
                {v.details_mismatch && <p className="text-xs font-semibold text-amber-600 flex items-center gap-1.5" data-testid={`visitor-mismatch-${v.id}`}><AlertTriangle size={12} /> Host details unverified — verify carefully</p>}
              </div>
              <div className="mt-4"><Actions v={v} /></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto sf-fade-up">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Visitor</TableHead><TableHead>Company</TableHead><TableHead>Phone</TableHead>
              <TableHead>Meet</TableHead><TableHead>Host Email</TableHead><TableHead>Cabin</TableHead>
              <TableHead>Status</TableHead><TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((v) => (
                <TableRow key={v.id} data-testid={`visitor-row-${v.id}`}>
                  <TableCell className="font-medium text-navy-900">{v.visitor_name}</TableCell>
                  <TableCell className="text-slate-500">{v.visitor_company}</TableCell>
                  <TableCell className="text-slate-500">{v.visitor_phone}</TableCell>
                  <TableCell className="text-slate-500">{v.person_to_meet}</TableCell>
                  <TableCell className="text-slate-500" data-testid={`visitor-host-email-${v.id}`}>{v.person_email || "—"}</TableCell>
                  <TableCell className="text-slate-500" data-testid={`visitor-cabin-${v.id}`}>{v.cabin_number || "—"}</TableCell>
                  <TableCell><StatusBadge status={v.status} /></TableCell>
                  <TableCell><Actions v={v} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manual Visitor Entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {user.role !== "BRANCH_ADMIN" && user.role !== "RECEPTIONIST" && (
              <div><Label>Branch *</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="mv-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {[["visitor_name", "Visitor Name *"], ["visitor_phone", "Phone *"], ["visitor_company", "Company *"], ["person_to_meet", "Person to Meet *"], ["person_phone", "Person Phone *"], ["person_email", "Person Email *"], ["cabin_number", "Cabin Number *"]].map(([k, l]) => (
              <div key={k}><Label>{l}</Label><Input data-testid={`mv-${k}`} className="mt-1 h-10" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
            ))}
            <div><Label>Purpose *</Label><Textarea data-testid="mv-purpose" className="mt-1" value={form.purpose || ""} onChange={(e) => setForm({ ...form, purpose: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-visitor-btn" className="bg-navy-900">{saving ? <Loader2 className="animate-spin" size={16} /> : "Submit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="visitor-detail-dialog">
          <DialogHeader><DialogTitle>Visitor Details</DialogTitle></DialogHeader>
          {detail && (
            <div>
              <div className="bg-navy-900 text-white rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <h3 className="font-display text-2xl font-bold">{detail.visitor_name}</h3>
                  <p className="text-blue-200/80 text-sm">{detail.visitor_company}</p>
                </div>
                <StatusBadge status={detail.status} />
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-4">
                {[["Visitor Phone", detail.visitor_phone], ["Person to Meet", detail.person_to_meet],
                  ["Host Email", detail.person_email], ["Host Phone", detail.person_phone],
                  ["Cabin Number", detail.cabin_number], ["Purpose", detail.purpose],
                  ["Requested", detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"],
                  ["Checked In", detail.checkin_time ? new Date(detail.checkin_time).toLocaleString() : "—"],
                  ["Checked Out", detail.checkout_time ? new Date(detail.checkout_time).toLocaleString() : "—"]].map(([k, val]) => (
                  <div key={k}>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{k}</p>
                    <p className="text-sm font-semibold text-navy-900 break-all">{val || "—"}</p>
                  </div>
                ))}
              </div>
              {passQr && (
                <div className="mt-5 flex flex-col items-center rounded-2xl border border-slate-200 bg-slate-50 py-4">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Visitor Pass</p>
                  <img src={passQr} alt="Visitor pass QR" className="w-40 h-40" data-testid="visitor-pass-qr" />
                  <p className="text-xs text-slate-400 mt-2">Reception can scan this QR to open the pass</p>
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-5">
                {detail.pass_link && <a href={detail.pass_link} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="detail-pass-link"><ExternalLink size={14} className="mr-1" /> Open Visitor Pass</Button></a>}
                {detail.approval_link && ["PENDING_EMPLOYEE_APPROVAL", "HOLD"].includes(detail.status) && <a href={detail.approval_link} target="_blank" rel="noreferrer"><Button variant="outline"><ExternalLink size={14} className="mr-1" /> Host Approval Link</Button></a>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
